import React, { useState, useEffect } from "react";

interface ReferralCodeEntryProps {
  onCodeSubmit: (code: string) => void;
  onSkip?: () => void;
  loading?: boolean;
  error?: string;
  initialCode?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "How do I join the referral program?",
    answer:
      "Connect at least one exchange in the bot to unlock your unique referral link. This helps prevent spam. Share your link to start earning points.",
  },
  {
    question: "How are points calculated?",
    answer:
      "You earn points for each person you refer, and for every trade they make. Your own trading volume also contributes to your total.",
  },
  {
    question: "What do points get me?",
    answer:
      "Points determine your rank on the leaderboard. The higher you rank, the more $TRIGGER you'll receive in the airdrop.",
  },
  {
    question: "Is there a limit to how many people I can refer?",
    answer:
      "No. You can refer as many people as you want — the more you bring, the more you earn.",
  },
  {
    question: "When will the airdrop happen?",
    answer:
      "The first airdrop will take place at TGE. Exact timing will be announced in the Telegram channel.",
  },
];

export const ReferralCodeEntry: React.FC<ReferralCodeEntryProps> = ({
  onCodeSubmit,
  // onSkip,
  loading = false,
  error,
  initialCode = "",
}) => {
  const [code, setCode] = useState(initialCode);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  // Update code when initialCode changes
  useEffect(() => {
    setCode(initialCode);
  }, [initialCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim()) {
      onCodeSubmit(code.trim());
    }
  };

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-primary bg-[url('/bg.png')] bg-top bg-no-repeat bg-contain text-text-primary relative overflow-hidden">
      {/* Content */}
      <div className="relative z-10 p-4 flex flex-col min-h-screen mt-[55vw] space-y-6">
        {/* Centered Content */}
        <div className="flex flex-col justify-center space-y-4">
          {/* Access Badge */}
          <div className="text-center">
            <div className="inline-flex items-center px-3 bg-[#13161b] border border-[#373a41] rounded-full">
              <span className="text-text-primary text-sm font-medium">
                Access is invite-only
              </span>
            </div>
          </div>

          {/* Title */}
          <div className="text-center max-w-sm mx-auto">
            <h2 className="text-xl leading-8 font-semibold text-text-primary leading-tight">
              Enter a referral code to get in and start trading before the pump.
            </h2>
          </div>

          {/* Referral Code Card */}
          <div className="mx-auto w-full max-w-sm">
            <div
              className="p-6 rounded-2xl border border-border"
              style={{
                background:
                  "linear-gradient(135deg, #00332180 0%, #00805300 100%)",
              }}
            >
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Have a Code?
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <div className="flex items-center w-full">
                    <img
                      src="/icons/key.svg"
                      alt="Code Icon"
                      className="w-5 h-5 absolute left-4 pointer-events-none"
                      style={{ zIndex: 2 }}
                    />
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter code"
                      className="w-full pl-11 pr-4 py-3 bg-primary border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-accent transition-colors"
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && (
                  <div className="text-red-400 text-sm text-center">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!code.trim() || loading}
                  className={`
                    ${!code.trim() || loading ? "" : "w-full py-2 text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-all duration-150 active:scale-[0.98] active:bg-accent/90 active:border-accent/70 flex items-center justify-center gap-2 animate-pulse-subtle"}
                    w-full py-2 text-sm leading-6 bg-accent hover:bg-accent-hover disabled:bg-accent/50 disabled:cursor-not-allowed text-text-primary font-medium rounded-lg transition-colors`}
                >
                  {loading ? "Verifying..." : "Submit"}
                </button>

                {/* {onSkip && (
                  <button
                    type="button"
                    onClick={onSkip}
                    disabled={loading}
                    className="w-full py-3 bg-secondary hover:bg-border disabled:bg-secondary/50 disabled:cursor-not-allowed text-text-secondary font-medium rounded-lg transition-colors"
                  >
                    Continue without referral code
                  </button>
                )} */}
              </form>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="">
          <span className="flex justify-start items-center gap-2">
            <img
              src="/icons/faq.svg"
              alt="Code Icon"
              className="w-5 h-5 pointer-events-none"
            />
            <h3 className="text-lg font-semibold text-text-primary ">F.A.Q.</h3>
          </span>

          {faqs.map((faq, index) => (
            <div key={index} className="rounded-lg">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full py-2 text-left flex items-center justify-between transition-colors"
              >
                <span className="text-text-primary font-medium">
                  {faq.question}
                </span>
                <img
                  src={
                    expandedFAQ === index
                      ? "/icons/close.svg"
                      : "/icons/open.svg"
                  }
                  alt="Toggle FAQ"
                  className={`faq-icon w-5 h-5 pointer-events-none transition-transform duration-300 ${expandedFAQ === index ? "rotate-180" : ""}`}
                />
              </button>

              <div
                className={`faq-item ${expandedFAQ === index ? "expanded" : ""}`}
              >
                <div className="faq-content">
                  <div className="pb-2">
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
